import {
  OnInit,
  OnDestroy,
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ResultOf } from '@graphql-typed-document-node/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, Validators } from '@angular/forms';
import {
  DataService,
  ModalService,
  SharedModule,
  NotificationService,
  TypedBaseDetailComponent,
} from '@vendure/admin-ui/core';
import { Observable, of, concat } from 'rxjs';
import {
  map,
  switchMap,
  take,
  distinctUntilChanged,
  shareReplay,
} from 'rxjs/operators';
import { unique } from '@vendure/common/lib/unique';
import {
  CatalogModule,
  ApplyFacetDialogComponent,
} from '@vendure/admin-ui/catalog';
import { BaseProductSelectedComponent } from '../base-product-selected/base-product-selected.component';
import { ImagePickerListComponent } from '../../common/image-picker-list/image-picker-list.component';
import {
  GetBaseProductDocument,
  BaseProductFragment,
  CreateBaseProductInput,
  Permission,
  UpdateBaseProductDocument,
  CreateBaseProductDocument,
} from '../../../gql/graphql';
import { Asset } from '@vendure/core';

@Component({
  templateUrl: './base-product-detail.component.html',
  styleUrls: ['./base-product-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SharedModule,
    CommonModule,
    CatalogModule,
    ImagePickerListComponent,
    BaseProductSelectedComponent,
  ],
})
export class BaseProductDetailComponent
  extends TypedBaseDetailComponent<typeof GetBaseProductDocument, 'baseProduct'>
  implements OnInit, OnDestroy
{
  protected readonly updatePermissions = [Permission.UpdateBaseProduct];

  detailForm = this.formBuilder.group({
    id: '',
    name: ['', Validators.required],
    size: [0, Validators.required],
    weight: [0, Validators.required],
    price: [0, Validators.required],
    asset: ['', Validators.required],
    facetValues: [[] as string[], Validators.required],
  });

  constructor(
    protected dataService: DataService,
    private formBuilder: FormBuilder,
    private modalService: ModalService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) {
    super();
  }

  onHttpRequest = false;
  facetValues$: Observable<BaseProductFragment['facetValues']>;

  setFormValues(
    entity: NonNullable<ResultOf<typeof GetBaseProductDocument>['baseProduct']>,
  ) {
    this.detailForm.patchValue({
      id: entity.id,
      asset: entity.asset.id,
      facetValues: entity.facetValues.map(e => e.id),
      name: entity.name,
      price: entity.price,
      size: entity.size,
      weight: entity.weight,
    });
  }

  removeProductFacetValue(facetValueId: string) {
    const productGroup = this.detailForm;
    const currentFacetValueIds = productGroup.value.facetValues ?? [];
    productGroup.patchValue({
      facetValues: currentFacetValueIds.filter(id => id !== facetValueId),
    });
    productGroup.markAsDirty();
  }

  onAsset(assets: Asset[]) {
    this.detailForm.controls.asset.setValue(
      assets.length > 0 ? <string>assets[0].id : null,
    );
    this.detailForm.controls.asset.markAsDirty();
    this.changeDetector.markForCheck();
  }

  selectFacetValue() {
    this.displayFacetValueModal().subscribe(facetValues => {
      if (facetValues) {
        const productGroup = this.detailForm;
        const currentFacetValues = productGroup.value.facetValues ?? [];
        productGroup.patchValue({
          facetValues: unique([...currentFacetValues, ...facetValues]),
        });
        productGroup.markAsDirty();
        this.changeDetector.markForCheck();
      }
    });
  }

  private displayFacetValueModal(): Observable<string[] | undefined> {
    return this.modalService
      .fromComponent(ApplyFacetDialogComponent, {
        size: 'lg',
        closable: true,
      })
      .pipe(map(facetValues => facetValues && facetValues.map(v => v.id)));
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  ngOnInit(): void {
    this.init();

    const baseProductFacetValues$ = this.isNew$.pipe(
      switchMap(isNew => {
        return isNew
          ? of([])
          : this.entity$.pipe(map(baseProduct => baseProduct.facetValues));
      }),
    );

    const baseProductGroup = this.detailForm;
    const formFacetValueIdChanges$ = baseProductGroup
      .get('facetValues')!
      .valueChanges.pipe(
        distinctUntilChanged(),
        switchMap(ids =>
          this.dataService.facet
            .getFacetValues({ filter: { id: { in: ids } } })
            .mapSingle(({ facetValues }) => facetValues.items),
        ),
        shareReplay(1),
      );

    console.log('Setting Change Listner');

    this.facetValues$ = concat(
      baseProductFacetValues$.pipe(take(1)),
      baseProductFacetValues$.pipe(switchMap(() => formFacetValueIdChanges$)),
    );
  }

  hideSubmit() {
    return (
      this.detailForm.invalid || this.detailForm.pristine || this.onHttpRequest
    );
  }
  save() {
    if (this.hideSubmit()) return;
    this.onHttpRequest = true;

    const values = this.detailForm.value;

    const input: CreateBaseProductInput = {
      facetValues: Array.isArray(values.facetValues) ? values.facetValues : [],
      name: values.name ?? '',
      price: values.price ?? 0,
      size: values.size ?? 0,
      weight: values.weight ?? 0,
      asset: values.asset ?? '',
    };

    if (this.entity?.id) {
      this.dataService
        .mutate(UpdateBaseProductDocument, {
          input: { id: values.id ?? '', ...input },
        })
        .subscribe({
          next: () => {
            this.validateReponse();
          },
          error: err => {
            this.onError(err);
          },
        });
    } else {
      this.dataService.mutate(CreateBaseProductDocument, { input }).subscribe({
        next: () => {
          this.validateReponse();
        },
        error: err => {
          this.onError(err);
        },
      });
    }
  }

  onError(err: { message: string }) {
    this.onHttpRequest = false;

    this.notificationService.error(err.message, {
      name: this.detailForm.value.name ?? '',
    });
  }

  validateReponse() {
    this.onHttpRequest = false;
    this.detailForm.markAsPristine();
    this.changeDetector.markForCheck();

    this.notificationService.success(
      this.entity?.id
        ? 'grupo-loja.baseproduct-update-success'
        : 'grupo-loja.baseproduct-create-success',
      {
        name: this.detailForm.value.name ?? '',
      },
    );
  }

  //TODO adicionar movimentação de estoque das variantes
  //TODO adicionar quantidade de venda de cada item listado em variante
}
